import { Megaphone } from "lucide-react";
import { Card, CardContent, CardHeader } from "../../ui/card";
import type { CampaignStatus } from "../../../lib/api";
import { formatScheduleStamp, statusChip } from "../../../lib/campaign-display";
import type {
  ProCampaignsListRender,
  ProCampaignSummary,
} from "../../../lib/pro-agent-types";

// list_campaigns read widget — the campaign twin of PersonCard.
// It renders the REAL campaigns.Service.List rows the tool returned, so the pro
// sees exactly what's in the book instead of a model-narrated table (which
// fabricated a phantom draft and mislabeled a "sending" campaign as "Sent").
// Read-only, no action buttons; delivery-status counts only (no open/click).

// A campaign's counts line: delivery status only. `sent` shows the sent count;
// `sending` says it's in progress; `scheduled` shows the send time; drafts and
// everything else stay quiet rather than print a fabricated "0 sent".
function metaLine(c: ProCampaignSummary): string | null {
  if (c.status === "scheduled" && c.scheduled_for) {
    return `Scheduled for ${formatScheduleStamp(c.scheduled_for)}`;
  }
  if (c.status === "sending") return "Delivery in progress…";
  if (c.status === "sent" || c.sent_count > 0) {
    const n = c.sent_count;
    const base = `${n} sent`;
    return c.excluded_count > 0 ? `${base} · ${c.excluded_count} skipped` : base;
  }
  return null;
}

function CampaignRow({ c }: { c: ProCampaignSummary }) {
  const chip = statusChip(c.status as CampaignStatus);
  const meta = metaLine(c);
  return (
    <div
      data-testid="campaigns-card-row"
      className="flex items-start justify-between gap-3 py-2.5"
    >
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-fg">
          {c.name.trim() || "Untitled campaign"}
        </p>
        {meta ? <p className="text-xs text-fg-muted">{meta}</p> : null}
      </div>
      <span
        className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${chip.className}`}
      >
        {chip.label}
      </span>
    </div>
  );
}

export function CampaignsListCard({ card }: { card: ProCampaignsListRender }) {
  const campaigns = card.campaigns ?? [];

  return (
    <Card data-testid="campaigns-card">
      <CardHeader className="gap-1">
        <div className="flex items-center gap-1.5">
          <Megaphone size={13} aria-hidden className="text-fg-muted" />
          <p className="text-sm font-semibold text-fg">
            Campaigns
            {campaigns.length > 0 ? (
              <span className="ml-1 font-normal text-fg-muted">
                ({campaigns.length})
              </span>
            ) : null}
          </p>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {campaigns.length === 0 ? (
          <p
            data-testid="campaigns-card-empty"
            className="border-t border-border-soft py-3 text-sm text-fg-muted"
          >
            No campaigns yet.
          </p>
        ) : (
          <div className="divide-y divide-border-soft border-t border-border-soft">
            {campaigns.map((c) => (
              <CampaignRow key={c.id} c={c} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
